import {
  ComponentProps,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApi } from 'shared_components/context';
import { ResponseError } from 'shared_components/generated/admin/runtime';
import { sendToast } from 'shared_components/utils/Toast';
import { useClientId } from 'shared_components/context/client';
import { useLocalStorageState } from 'shared_components/hooks/useLocalStorageState';
import {
  UNKNOWN_AFTER_CONSECUTIVE_FAILURES,
  canCancelCollection,
  collectionIndicator,
  deriveCollectionStatus,
  elapsedSeconds,
  isActiveCollectionStatus,
  isTriggerDisabled,
  nextPollDelayMs,
  shouldPollCollection,
  type CollectionIndicator,
  type CollectionPhase,
  type CollectionStatus,
} from '../artistIdCollectionModel';

export type SourceId = {
  id: string;
  original_name: string;
  valid: boolean;
  rejected: boolean;
  approved: boolean;
  created_by_system: boolean;
};

export type DiscographyId = {
  id: string;
  stageName: string;
  type: string;
  source_ids: {
    deezer: SourceId[];
    discogs: SourceId[];
    musicbrainz: SourceId[];
    spotify: SourceId[];
  };
  manualStageName: boolean;
  is_permanent_member?: boolean;
  is_main_performer?: boolean;
  linked_alias_id?: string | null;
  created_by_system: boolean;
};

export type DiscographySourceType = keyof DiscographyId['source_ids'];

/**
 * The collected IDs, or an explicit admission that we do not know them.
 *
 * `known: false` is not the same as an empty list, and the difference decides whether the
 * admin is warned before a collection overwrites IDs they curated by hand.
 */
export type CollectedIdsSnapshot =
  | { known: true; ids: DiscographyId[] }
  | { known: false };

type DiscographyIdsContexType = {
  /** Coarse UI status derived from the backend run report. See `artistIdCollectionModel`. */
  status: CollectionStatus;
  /** Backend lifecycle phase — the source of truth behind `status`. */
  phase: CollectionPhase;
  /** Label / detail / spinner / attention flags for the current status. */
  indicator: CollectionIndicator;
  /** Whole seconds since the run was requested, or null when nothing is running. */
  elapsed: number | null;
  /** The backend has seen no progress for this phase's budget. */
  isStalled: boolean;
  errorDetail: string | null;
  /** True while a collection is in flight *or* while we cannot see the backend. */
  triggerDisabled: boolean;
  canCancel: boolean;
  source: string;
  sourceId: string;
  autoCollectIds: (
    clientId: string,
    sourceId: string,
    source: string
  ) => Promise<void>;
  cancelAutoCollectIds: (clientId: string) => Promise<void>;
  cancelAutoCollectIdsInProgress: boolean;
  collectedDiscographyIds: DiscographyId[];
  /**
   * Re-read the collected IDs. Resolves once the new list is in state, so a
   * caller showing a pending indicator can await it and hand over directly to
   * the refreshed row rather than to a stale one.
   */
  getCollectedDiscographyIds: (
    clientId: string
  ) => Promise<DiscographyId[] | null>;
  /**
   * The collected IDs, fetching them first if we have not looked yet for this artist.
   *
   * Callers deciding whether to warn about overwriting existing IDs must use this rather
   * than reading `collectedDiscographyIds`, which is `[]` both when the artist has none and
   * when the fetch has simply not happened yet — a difference that decides whether a
   * destructive confirmation is shown at all.
   */
  ensureCollectedDiscographyIds: (
    clientId: string
  ) => Promise<CollectedIdsSnapshot>;
};

const DiscographyIdsContex = createContext<
  DiscographyIdsContexType | undefined
>(undefined);

const DiscographyIdsProviderImplementation = ({
  children,
  clientId,
}: {
  readonly children: React.ReactNode;
  readonly clientId: string;
}) => {
  // The run report is server state; the client only mirrors it. The old hook kept the
  // *status* in localStorage, so a bad reading ("nothing is running") outlived the reload
  // that would have corrected it.
  const [status, setStatus] = useState<CollectionStatus>('unknown');
  const [phase, setPhase] = useState<CollectionPhase>(null);
  const [isStalled, setIsStalled] = useState(false);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [requestedAt, setRequestedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Source/sourceId stay local: they are the admin's search selection, not run state.
  const [source, setSource] = useLocalStorageState<
    DiscographyIdsContexType['source']
  >('source', '', {
    namespace: clientId,
  });
  const [sourceId, setSourceId] = useLocalStorageState<
    DiscographyIdsContexType['sourceId']
  >('sourceId', '', {
    namespace: clientId,
  });
  const [collectedDiscographyIds, setCollectedDiscographyIds] = useState<
    DiscographyIdsContexType['collectedDiscographyIds']
  >([]);
  const [cancelAutoCollectIdsInProgress, setCancelAutoCollectIdsInProgress] =
    useState(false);

  // A trigger or cancel request is in flight. Suppresses a second one, and suppresses the
  // poll loop, whose answer during that window describes the run being replaced.
  const mutating = useRef(false);
  // Consecutive failed polls. One hiccup must not change what the admin sees; a sustained
  // outage must — and must also back the poll off rather than hammering a sick backend.
  const consecutiveFailures = useRef(0);
  // Bumped on every artist switch and on every trigger/cancel. A status response whose epoch
  // no longer matches is discarded: without this, a slow poll issued for the previous artist
  // (or before the click) lands afterwards and overwrites the newer state with the older one
  // — reintroducing exactly the "UI says nothing is running" flicker this whole change fixes.
  const epoch = useRef(0);
  // Whether `collectedDiscographyIds` reflects a completed fetch for the current artist. The
  // overwrite confirmation must not be skipped merely because we have not looked yet.
  const collectedIdsLoaded = useRef(false);
  // Consecutive 404s specifically. Distinguishes "this backend has no such endpoint" from
  // "one request went astray"; see refreshRunStatus.
  const consecutiveNotFound = useRef(0);
  // Whether the last status we saw was an active run, so we know when one has just ended.
  const wasActive = useRef(false);

  const { adminApi: api } = useApi();

  // `useLocalStorageState` builds a brand-new `setValue` closure on every render, and
  // `useApi` makes no identity promise either. Every callback below is both used from async
  // code and named in an effect's dependency array, so it has to be *stable*. Listing these
  // directly as deps gave each callback a fresh identity per render → the reset effect
  // re-ran → it set state → re-render → "Maximum update depth exceeded" on mount, plus a
  // status request per iteration. Holding them in a ref keeps the callbacks stable while
  // still reading the current values, which is what a stale closure would otherwise cost.
  const latest = useRef({ api, source, sourceId, setSource, setSourceId });
  latest.current = { api, source, sourceId, setSource, setSourceId };

  const getCollectedDiscographyIds = useCallback(async (clientId: string) => {
    const issuedAt = epoch.current;
    try {
      const data =
        await latest.current.api.retrieveCollectedIdsAutoCollectedIds({
          userId: clientId,
        });
      // Belongs to the artist we were looking at before the switch. Adopting it would show
      // one artist's discography under another's name *and* mark the IDs "loaded", which
      // silently suppresses the overwrite confirmation for the artist now on screen.
      if (issuedAt !== epoch.current) return null;
      const list = data.collectedIds as unknown as any[];
      const mapped = list.map((item) => ({
        ...item,
        source_ids: {
          discogs: [],
          deezer: [],
          musicbrainz: [],
          spotify: [],
          ...item.source_ids,
        },
      })) as DiscographyId[];
      collectedIdsLoaded.current = true;
      setCollectedDiscographyIds(mapped);
      return mapped;
    } catch (err) {
      if (issuedAt !== epoch.current) return null;
      console.error(err);
      return null;
    }
  }, []);

  /**
   * Read the run report and mirror it into state.
   *
   * A rejected request does *not* fall through to "nothing is running". It bumps the
   * failure counter, and only a run of failures moves the UI to `unknown` — which still
   * keeps the button disabled and the poll loop alive.
   */
  const refreshRunStatus = useCallback(async (clientId: string) => {
    const issuedAt = epoch.current;
    try {
      const data =
        await latest.current.api.retrieveAutoCollectIdsJobRunStatusArtistIdCollectionRunStatus(
          { userId: clientId }
        );
      // A response from before the last artist switch / click is stale by definition.
      if (issuedAt !== epoch.current) return;
      consecutiveFailures.current = 0;
      consecutiveNotFound.current = 0;
      setStatus(deriveCollectionStatus(data));
      setPhase((data.phase ?? null) as CollectionPhase);
      setIsStalled(Boolean(data.isStalled));
      setErrorDetail(data.errorDetail ?? null);
      // Store the instant, not the Date: the generated client builds a new Date on every
      // response, and two equal Dates still fail `Object.is`, so an unchanged poll answer
      // would re-render the provider and the whole discography subtree every 3s.
      setRequestedAt(data.requestedAt ? data.requestedAt.getTime() : null);
      // Only write when it actually changed. `useLocalStorageState` writes through to
      // localStorage unconditionally, and re-applying this every poll also stamps the last
      // run's source over the search selection the admin is making right now.
      if (data.source && data.source !== latest.current.source) {
        latest.current.setSource(data.source);
      }
      if (data.sourceId && data.sourceId !== latest.current.sourceId) {
        latest.current.setSourceId(data.sourceId);
      }
    } catch (err) {
      if (issuedAt !== epoch.current) return;
      const notFound =
        err instanceof ResponseError && err.response.status === 404;
      consecutiveFailures.current += 1;
      consecutiveNotFound.current = notFound
        ? consecutiveNotFound.current + 1
        : 0;
      if (!notFound) console.error(err);

      // A *sustained* 404 means this bundle is talking to a backend that predates the
      // endpoint, and the admin should get a usable button rather than a dead one. A single
      // 404 must not: a rolling deploy or a momentarily mis-routed request produces one too,
      // and `idle` is terminal — it stops the poll loop and re-enables the trigger, which is
      // precisely the "UI says nothing is running" bug, one bad response later.
      if (consecutiveNotFound.current >= UNKNOWN_AFTER_CONSECUTIVE_FAILURES) {
        setStatus('idle');
        setPhase(null);
        return;
      }
      if (consecutiveFailures.current >= UNKNOWN_AFTER_CONSECUTIVE_FAILURES) {
        setStatus('unknown');
      }
    }
  }, []);

  // Read the server's view on mount / artist switch, before any polling decision.
  useEffect(() => {
    // Every piece of per-artist state, not just the status — leaving `errorDetail` or
    // `collectedDiscographyIds` behind shows one artist's run details under another's name.
    epoch.current += 1;
    consecutiveFailures.current = 0;
    consecutiveNotFound.current = 0;
    collectedIdsLoaded.current = false;
    mutating.current = false;
    wasActive.current = false;
    setStatus('unknown');
    setPhase(null);
    setIsStalled(false);
    setErrorDetail(null);
    setRequestedAt(null);
    setCollectedDiscographyIds([]);
    refreshRunStatus(clientId);
    // Unconditionally, and independently of the run status. Gating this on the status made
    // a status-endpoint outage empty the ID table for an artist with thousands of rows —
    // one broken endpoint taking down the page's entire read path.
    getCollectedDiscographyIds(clientId);
  }, [clientId, refreshRunStatus, getCollectedDiscographyIds]);

  // Re-read the collected IDs when a run finishes. The file is only written at the end, so
  // there is nothing to see until an active run goes terminal.
  useEffect(() => {
    if (isActiveCollectionStatus(status)) {
      wasActive.current = true;
      return;
    }
    if (!wasActive.current) return;
    wasActive.current = false;
    getCollectedDiscographyIds(clientId);
  }, [status, clientId, getCollectedDiscographyIds]);

  // The poll loop. It keeps running while the state is in doubt, and reschedules itself
  // from a ref-based delay so a backend outage backs off instead of hammering.
  useEffect(() => {
    if (!shouldPollCollection(status)) return;

    let timeout: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      // A trigger or cancel is mid-flight. Polling now reads the *previous* run's terminal
      // state — "nothing is running" in the middle of starting something — which drops the
      // poll loop and re-enables the button under the admin's cursor. The mutation runs its
      // own refresh when it lands.
      if (!mutating.current) await refreshRunStatus(clientId);
      if (cancelled) return;
      timeout = setTimeout(tick, nextPollDelayMs(consecutiveFailures.current));
    };

    timeout = setTimeout(tick, nextPollDelayMs(consecutiveFailures.current));

    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [status, clientId, refreshRunStatus]);

  // Drives the elapsed clock. Runs only while something is in flight, so an idle page does
  // no work.
  useEffect(() => {
    if (!isActiveCollectionStatus(status)) return;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [status]);

  const autoCollectIds = useCallback(
    async (clientId: string, sourceId: string, source: string) => {
      if (mutating.current) return;
      mutating.current = true;
      // Invalidate any poll already in flight: its answer predates this click.
      epoch.current += 1;

      // Disable the button on the click, not when the response lands. The wait for the
      // response is exactly the window in which the admin used to click again.
      setStatus('triggering');
      setPhase(null);
      setIsStalled(false);
      setErrorDetail(null);
      setRequestedAt(Date.now());
      latest.current.setSource(source);
      latest.current.setSourceId(sourceId);

      try {
        await latest.current.api.autoCollectIdsArtistIdCollectionRunStatus({
          userId: clientId,
          sourceId,
          source,
        });
      } catch (err) {
        // 409 means a collection is already running for this artist — the backend refusing
        // to cancel-and-relaunch. Not an error to show; the refresh below adopts the
        // server's state and the admin sees the run that is already going.
        const conflict =
          err instanceof ResponseError && err.response.status === 409;
        if (!conflict) {
          console.error(err);
          sendToast({
            text: 'Could not start collecting IDs. Please try again.',
            variant: 'error',
          });
        }
      } finally {
        mutating.current = false;
      }

      consecutiveFailures.current = 0;
      consecutiveNotFound.current = 0;
      await refreshRunStatus(clientId);
    },
    [refreshRunStatus]
  );

  const cancelAutoCollectIds = useCallback(async () => {
    if (mutating.current) return;
    mutating.current = true;
    setCancelAutoCollectIdsInProgress(true);
    epoch.current += 1;
    // Reflect the request immediately, but as "cancelling" — not as "nothing is running",
    // which is what let the admin start a second run on top of a still-terminating one.
    setStatus('cancelling');
    setPhase('CANCELLING');

    try {
      await latest.current.api.cancelAutoCollectIdsArtistIdCollectionRunStatus({
        userId: clientId,
      });
    } catch (ex) {
      console.error(ex);
      sendToast({
        text: 'Could not cancel the collection. Please try again.',
        variant: 'error',
      });
    } finally {
      mutating.current = false;
      setCancelAutoCollectIdsInProgress(false);
    }

    consecutiveFailures.current = 0;
    consecutiveNotFound.current = 0;
    await refreshRunStatus(clientId);
  }, [clientId, refreshRunStatus]);

  const ensureCollectedDiscographyIds = useCallback(
    async (clientId: string): Promise<CollectedIdsSnapshot> => {
      if (collectedIdsLoaded.current) {
        return { known: true, ids: collectedDiscographyIds };
      }
      const fetched = await getCollectedDiscographyIds(clientId);
      // A failed or superseded fetch is not evidence of "no existing IDs" — say so rather
      // than returning an empty list the caller would read as "nothing to overwrite".
      return fetched ? { known: true, ids: fetched } : { known: false };
    },
    [collectedDiscographyIds, getCollectedDiscographyIds]
  );

  const elapsed = useMemo(
    () =>
      isActiveCollectionStatus(status)
        ? elapsedSeconds(requestedAt, now)
        : null,
    [status, requestedAt, now]
  );

  const indicator = useMemo(
    () => collectionIndicator(status, { elapsed, isStalled, errorDetail }),
    [status, elapsed, isStalled, errorDetail]
  );

  const value = useMemo(() => {
    return {
      status,
      phase,
      indicator,
      elapsed,
      isStalled,
      errorDetail,
      triggerDisabled: isTriggerDisabled(status),
      canCancel: canCancelCollection(status),
      source,
      sourceId,
      collectedDiscographyIds: collectedDiscographyIds ?? [],
      autoCollectIds,
      cancelAutoCollectIds,
      cancelAutoCollectIdsInProgress,
      getCollectedDiscographyIds,
      ensureCollectedDiscographyIds,
    };
  }, [
    status,
    phase,
    indicator,
    elapsed,
    isStalled,
    errorDetail,
    source,
    sourceId,
    collectedDiscographyIds,
    autoCollectIds,
    cancelAutoCollectIds,
    cancelAutoCollectIdsInProgress,
    getCollectedDiscographyIds,
    ensureCollectedDiscographyIds,
  ]);

  return (
    <DiscographyIdsContex.Provider value={value}>
      {children}
    </DiscographyIdsContex.Provider>
  );
};

export const DiscographyIdsProvider = (
  props: Omit<
    ComponentProps<typeof DiscographyIdsProviderImplementation>,
    'clientId'
  >
) => {
  const { clientId } = useClientId();
  if (!clientId) return null;

  return (
    <DiscographyIdsProviderImplementation {...props} clientId={clientId} />
  );
};

export const useDiscographyIds = () => {
  const context = useContext(DiscographyIdsContex);

  if (!context) throw new Error('Cannot use DiscographyIds without provider');

  return context;
};
